import { isRecord } from './advisoryProvenance';

export interface AdvisoryAuditFinding {
  kind: string;
  severity: 'critical' | 'high' | 'notice';
  answerKey: string;
  detail: string;
  downstream: readonly string[];
}

export interface AdvisoryAuditReport {
  findings: readonly AdvisoryAuditFinding[];
  audited: readonly string[];
  skipped: readonly string[];
  incomplete: readonly string[];
}

export type AdvisoryAuditState =
  | { status: 'unavailable' | 'loading' | 'failed' | 'invalid' }
  | { status: 'incomplete' | 'ready'; report: AdvisoryAuditReport };

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string' && item.trim().length > 0);

/**
 * PR #129 records incomplete inputs; it still carries no answer revisions.
 * The delivery adapter must also establish that this report belongs to the
 * currently displayed answers before it can attest completeness.
 */
export function mapAdvisoryAuditReport(
  raw: unknown,
  completeness: 'incomplete' | 'complete' = 'incomplete'
): AdvisoryAuditState {
  if (
    !isRecord(raw) ||
    typeof raw.clean !== 'boolean' ||
    !Array.isArray(raw.findings) ||
    !strings(raw.audited) ||
    !strings(raw.skipped) ||
    (raw.incomplete !== undefined && !strings(raw.incomplete)) ||
    !isRecord(raw.bySeverity)
  )
    return { status: 'invalid' };

  const findings: AdvisoryAuditFinding[] = [];
  for (const value of raw.findings) {
    if (
      !isRecord(value) ||
      typeof value.kind !== 'string' ||
      !value.kind.trim() ||
      (value.severity !== 'critical' && value.severity !== 'high' && value.severity !== 'notice') ||
      typeof value.answerKey !== 'string' ||
      !value.answerKey.trim() ||
      typeof value.detail !== 'string' ||
      !strings(value.downstream)
    )
      return { status: 'invalid' };
    findings.push({
      kind: value.kind,
      severity: value.severity,
      answerKey: value.answerKey,
      detail: value.detail,
      downstream: [...value.downstream],
    });
  }
  const bySeverity = raw.bySeverity;
  const incomplete = raw.incomplete === undefined ? [] : (raw.incomplete as string[]);
  if (
    raw.clean !== (findings.length === 0 && incomplete.length === 0) ||
    ['critical', 'high', 'notice'].some(
      severity =>
        bySeverity[severity] !== findings.filter(finding => finding.severity === severity).length
    )
  )
    return { status: 'invalid' };

  return {
    status:
      completeness === 'complete' && incomplete.length === 0 && raw.audited.length > 0
        ? 'ready'
        : 'incomplete',
    report: {
      findings,
      audited: [...raw.audited],
      skipped: [...raw.skipped],
      incomplete: [...incomplete],
    },
  };
}

export function findingsForAnswer(state: AdvisoryAuditState | undefined, answerKey: string) {
  return state && 'report' in state
    ? state.report.findings.filter(
        finding => finding.answerKey === answerKey || finding.downstream.includes(answerKey)
      )
    : [];
}
